import config from 'syft-acp-core/config'
import { getAuthHeaders } from 'syft-acp-core/api/call'
import { COUNTRY_CODE_PLATFORMS } from 'syft-acp-core/lib/i18n'
import { ShiftIssue, ShiftIssuesEntity } from './types'

// Not an `apiRequest`: this service is not flex-core, so it has its own base URL and an
// /api/v1 path, neither of which `apiURL` can build.
const noIssues = (): { payload: ShiftIssuesEntity } => ({ payload: { issues: [] } })

const SLACK_HOST = /(^|\.)slack\.com$/

// The banner puts this straight in an `href`, and React 16 renders a `javascript:` URL after
// only warning. Anything not an https Slack link becomes null, which renders as no link.
const safePermalink = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  try {
    const { protocol, hostname } = new URL(value)

    return protocol === 'https:' && SLACK_HOST.test(hostname) ? value : null
  } catch {
    return null
  }
}

// `status` is deliberately unchecked: nothing renders it, and rejecting an unrecognised one
// would silently hide a real issue.
const isRenderable = (value: unknown): value is ShiftIssue => {
  const issue = value as Partial<ShiftIssue> | null

  return (
    !!issue &&
    typeof issue.check === 'string' &&
    typeof issue.title === 'string' &&
    typeof issue.explanation === 'string'
  )
}

// A single malformed entry would otherwise crash the shift tab, which the catch below cannot
// help with because the body parsed fine.
const toIssues = (body: unknown): ShiftIssue[] => {
  const raw = (body as { issues?: unknown } | null)?.issues

  return Array.isArray(raw)
    ? raw
        .filter(isRenderable)
        .map(issue => ({ ...issue, slack_permalink: safePermalink(issue.slack_permalink) }))
    : []
}

// Without this a black-holed host hangs the request for minutes: Chrome applies no fetch
// timeout of its own. `AbortController` rather than `AbortSignal.timeout`, which needs
// Chrome 103 / Safari 16 and would break the Chrome 93 / Safari 15 floor in .browserslistrc.
const TIMEOUT_MS = 5000

export const fetchShiftIssues = async ({ shiftID }: { shiftID: number }) => {
  if (!config.dataHealthUrl || !shiftID) {
    return noIssues()
  }

  const baseUrl = config.dataHealthUrl.replace(/\/$/, '')
  const platform = COUNTRY_CODE_PLATFORMS[config.region]
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const response = await fetch(`${baseUrl}/api/v1/platforms/${platform}/shifts/${shiftID}/issues`, {
      // The service requires a flex-core admin token, and sending one makes this request
      // preflighted. Cast because `getAuthHeaders` is JS typed `@returns {Object}`.
      headers: getAuthHeaders() as Record<string, string>,
      signal: controller.signal,
    })
    if (!response.ok) {
      return noIssues()
    }
    const body = await response.json()

    return { payload: { issues: toIssues(body) } }
  } catch {
    // Advisory banner: a data-health outage or timeout must not surface on the shift page.
    return noIssues()
  } finally {
    clearTimeout(timeout)
  }
}
